import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DateRange, MatCalendar, MatCalendarCellCssClasses, MatCalendarUserEvent } from '@angular/material/datepicker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputObservable } from '@y42/angular/util';
import { DateTimeService } from '@y42/date-time';
import { filter, map, merge, Observable, Subject, tap, withLatestFrom } from 'rxjs';

enum SelectAction {
  StartDateSelected = 1,
  StartDateUnselected,
  EndDateSelected,
  SingleDayRangeSelected,
}

@UntilDestroy()
@Component({
  selector: 'y42-date-picker[selectedRange]',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent {
  constructor(private readonly dateTimeService: DateTimeService) {
    const selectRange$ = this.dateClicked$$.pipe(
      filter(Boolean),
      map(({ value }) => value as Date),
      withLatestFrom(this.selectedRange$),
      map(([selectedDate, selectedRange]) => this.handleSelection(selectedDate, selectedRange)),
      tap((selectedDate) => {
        this.dateChanged.emit(selectedDate);
      }),
    );

    const rerenderCalendarOnRangeChange$ = merge(
      this.goToStartMonthOnStartAtChange$,
      this.selectedRange$.pipe(filter(Boolean)),
    ).pipe(
      tap(() => {
        // Force calendar to rerender and call getDateClass
        this.calendar?.updateTodaysDate();
      }),
    );

    merge(selectRange$, rerenderCalendarOnRangeChange$).pipe(untilDestroyed(this)).subscribe();
  }

  @Input()
  @InputObservable()
  selectedRange!: DateRange<Date>;
  selectedRange$!: Observable<DateRange<Date>>;

  @Input()
  @InputObservable()
  startAt: Date | null = null;
  startAt$!: Observable<Date | null>;

  @Input()
  minDate: Date | null = null;

  @Input()
  maxDate: Date | null = null;

  @Input()
  pickSingleDate = false;

  @Output()
  dateChanged = new EventEmitter<DateRange<Date>>();

  @ViewChild(MatCalendar)
  calendar!: MatCalendar<Date>;

  private readonly dateClicked$$ = new Subject<MatCalendarUserEvent<Date | null>>();

  private readonly goToStartMonthOnStartAtChange$ = this.startAt$.pipe(
    filter((startAt) => !!startAt && !!this.calendar),
    tap((date) => {
      this.calendar.activeDate = date as Date;
    }),
  );

  handleDateClick(value: MatCalendarUserEvent<Date | null>): void {
    this.dateClicked$$.next(value);
  }

  // Arrow function is used to have proper 'this' context
  protected readonly getDateClass = (date: Date): MatCalendarCellCssClasses => {
    const { start, end } = this.selectedRange ?? {};
    if (!(start && end)) {
      return '';
    }
    if (
      this.dateTimeService.isBetween(date, this.dateTimeService.startOfDay(start), this.dateTimeService.startOfDay(end))
    ) {
      return 'mat-calendar-body-in-range';
    }
    if (this.dateTimeService.isSame(date, start, 'days')) {
      return 'mat-calendar-body-in-range mat-calendar-body-range-start';
    }
    if (this.dateTimeService.isSame(date, end, 'days')) {
      return 'mat-calendar-body-in-range mat-calendar-body-range-end';
    }
    return '';
  };

  private handleSelection(selectedDate: Date, currentRange: DateRange<Date>): DateRange<Date> {
    const action = this.calculateSelectAction(selectedDate, currentRange);

    switch (action) {
      case SelectAction.StartDateSelected:
        return new DateRange(selectedDate, null);
      case SelectAction.StartDateUnselected:
        return new DateRange<Date>(null, null);
      case SelectAction.EndDateSelected:
        return new DateRange(currentRange.start, selectedDate);
      case SelectAction.SingleDayRangeSelected:
        return new DateRange(selectedDate, new Date(selectedDate.getTime()));
    }
  }

  private calculateSelectAction(selectedDate: Date, { start, end }: DateRange<Date>): SelectAction {
    const isEndDatePending = start && !end;
    const isRangeSelected = start && end;

    if (
      isRangeSelected &&
      this.dateTimeService.isSame(start, end, 'days') &&
      this.dateTimeService.isSame(selectedDate, start, 'days')
    ) {
      return SelectAction.StartDateUnselected;
    }
    if (this.pickSingleDate || (isEndDatePending && this.dateTimeService.isSame(selectedDate, start, 'days'))) {
      return SelectAction.SingleDayRangeSelected;
    }
    if (isEndDatePending && this.dateTimeService.isAfter(selectedDate, start)) {
      return SelectAction.EndDateSelected;
    }

    return SelectAction.StartDateSelected;
  }
}
